import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Clock, Users, Hourglass } from 'lucide-react';

/**
 * ExpiryCardPremium
 *
 * Modern, calm urgency card shown above driver offers in the "Select a Driver
 * & Pay" modal. Replaces the older inline red banner.
 *
 *   - Big tabular-nums HH:MM:SS countdown
 *   - "X drivers are waiting for your decision" + context line
 *   - Progress bar that drains over the lifetime of the request
 *   - Three dynamic colour states (green / amber / red) computed from the
 *     remaining time AND the original request window (24-hour scheduled jobs
 *     vs 3-hour same-day jobs use different thresholds).
 *
 * Props:
 *   - expiresAt    ISO timestamp the request expires (required)
 *   - createdAt    ISO timestamp the request was posted (optional, used to
 *                  compute the original window for the progress bar)
 *   - driverCount  Number of submitted offers (defaults to 0)
 *   - onExpire     Called once when the timer hits zero
 *
 * Auto re-renders every second; cleans up its interval on unmount.
 */
export default function ExpiryCardPremium({ expiresAt, createdAt, driverCount = 0, onExpire }) {
  const [now, setNow] = useState(() => Date.now());
  const firedRef = useRef(false);

  useEffect(() => {
    if (!expiresAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const target = expiresAt ? new Date(expiresAt).getTime() : null;
  const diffMs = target != null ? target - now : 0;
  const isExpired = expiresAt && diffMs <= 0;
  const totalSec = Math.max(0, Math.floor(diffMs / 1000));

  useEffect(() => {
    if (isExpired && !firedRef.current && typeof onExpire === 'function') {
      firedRef.current = true;
      onExpire();
    }
  }, [isExpired, onExpire]);

  // Window size in seconds — needed for the progress bar AND the colour
  // thresholds (3-hour same-day vs 24-hour scheduled jobs).
  const windowSec = useMemo(() => {
    if (!expiresAt) return 0;
    if (createdAt) {
      const win = Math.max(
        60,
        Math.round((new Date(expiresAt).getTime() - new Date(createdAt).getTime()) / 1000)
      );
      return win;
    }
    // Fallback: infer from remaining time — same-day jobs typically have
    // <= 3h windows; everything else assumed to be the standard 24h window.
    return totalSec <= 3 * 3600 ? 3 * 3600 : 24 * 3600;
  }, [expiresAt, createdAt, totalSec]);

  const isSameDay = windowSec <= 6 * 3600;

  // Thresholds (seconds remaining):
  //   24-hour window: > 6h green, 1–6h amber, < 1h red
  //   3-hour  window: > 1h green, 30–60m amber, < 30m red
  const state = useMemo(() => {
    if (isExpired) return 'expired';
    if (isSameDay) {
      if (totalSec > 60 * 60) return 'green';
      if (totalSec > 30 * 60) return 'amber';
      return 'red';
    }
    if (totalSec > 6 * 3600) return 'green';
    if (totalSec > 1 * 3600) return 'amber';
    return 'red';
  }, [isExpired, isSameDay, totalSec]);

  if (!expiresAt) return null;

  if (isExpired) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-red-50 border border-red-200 text-red-800 text-sm font-semibold"
        data-testid="expiry-card-expired"
      >
        <Hourglass className="w-4 h-4 flex-shrink-0" />
        Request expired
      </div>
    );
  }

  // Theme tokens by state
  const theme = {
    green: {
      cardBg: 'bg-emerald-50/70',
      cardBorder: 'border-emerald-100',
      timer: 'text-[#1B5E20]',
      icon: 'text-[#1B5E20]',
      iconBg: 'bg-white border-emerald-200',
      label: 'text-[#1B5E20]',
      legendMute: 'text-emerald-700/70',
      barTrack: 'bg-emerald-100',
      barFill: 'bg-[#2E7D32]',
      bodyText: 'text-gray-700',
      highlight: 'text-[#1B5E20]',
    },
    amber: {
      cardBg: 'bg-amber-50/70',
      cardBorder: 'border-amber-100',
      timer: 'text-[#E65100]',
      icon: 'text-[#E65100]',
      iconBg: 'bg-white border-amber-200',
      label: 'text-[#E65100]',
      legendMute: 'text-amber-700/70',
      barTrack: 'bg-amber-100',
      barFill: 'bg-[#F57C00]',
      bodyText: 'text-gray-700',
      highlight: 'text-[#E65100]',
    },
    red: {
      cardBg: 'bg-red-50/70',
      cardBorder: 'border-red-100',
      timer: 'text-[#C62828]',
      icon: 'text-[#C62828]',
      iconBg: 'bg-white border-red-200',
      label: 'text-[#C62828]',
      legendMute: 'text-red-700/70',
      barTrack: 'bg-red-100',
      barFill: 'bg-[#C62828]',
      bodyText: 'text-gray-700',
      highlight: 'text-[#C62828]',
    },
  }[state];

  // Headline message
  const headline =
    state === 'green'
      ? (isSameDay
          ? '📅 Same-day request — driver offers are open.'
          : '📅 This request will expire in 24 hours if no driver is selected.')
      : state === 'amber'
        ? '⏳ Driver offers are still available. Choose a driver before the request expires.'
        : '⚠️ This request will expire soon if no driver is selected.';

  // Drivers waiting subline (more calm — no pushy language)
  const driversLine = driverCount > 0
    ? `${driverCount} ${driverCount === 1 ? 'driver is' : 'drivers are'} waiting for your decision`
    : 'Drivers are reviewing your request';
  const driversBody = driverCount > 0
    ? 'Review offers, ratings and driver profiles before paying.'
    : 'You\'ll see offers appear here as drivers respond.';

  // Progress bar — drains from 100% (just posted) → 0% (about to expire).
  const pctRemaining = Math.max(0, Math.min(100, (totalSec / windowSec) * 100));

  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, '0');

  // Plain-English remaining time used in the legend ("23 hours 45 minutes")
  const remainingPretty = (() => {
    if (h >= 1) {
      const hLabel = `${h} ${h === 1 ? 'hour' : 'hours'}`;
      return m > 0 ? `${hLabel} ${m} ${m === 1 ? 'minute' : 'minutes'}` : hLabel;
    }
    if (m >= 1) return `${m} ${m === 1 ? 'minute' : 'minutes'}`;
    return `${s} seconds`;
  })();

  return (
    <div
      className={`rounded-xl border ${theme.cardBorder} ${theme.cardBg} p-4 sm:p-5`}
      data-testid="expiry-card-premium"
      data-state={state}
      role="timer"
      aria-live="polite"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Left: clock icon + countdown digits */}
        <div className="flex items-center gap-3 sm:gap-4 sm:pr-4 sm:border-r sm:border-gray-200/70">
          <span className={`flex-shrink-0 w-12 h-12 rounded-full border-2 ${theme.iconBg} flex items-center justify-center`}>
            <Clock className={`w-6 h-6 ${theme.icon}`} strokeWidth={2} />
          </span>
          <div>
            <p className={`text-[11px] font-semibold uppercase tracking-wider ${theme.label}`}>
              Request expires in
            </p>
            <p
              className={`text-2xl sm:text-3xl font-extrabold tabular-nums leading-none mt-0.5 ${theme.timer}`}
              data-testid="expiry-card-timer"
            >
              {pad(h)}:{pad(m)}:{pad(s)}
            </p>
            <p className={`text-[10px] font-semibold tracking-[0.25em] mt-1 ${theme.legendMute}`}>
              <span className="inline-block w-[3.4em] text-center">HR</span>
              <span className="inline-block w-[3.4em] text-center">MIN</span>
              <span className="inline-block w-[3.4em] text-center">SEC</span>
            </p>
          </div>
        </div>

        {/* Right: drivers waiting + headline */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-bold flex items-center gap-1.5 ${theme.bodyText}`}>
            <Users className={`w-4 h-4 ${theme.icon}`} />
            <span data-testid="expiry-card-drivers-line">{driversLine}</span>
          </p>
          <p className={`text-xs sm:text-sm mt-1 leading-snug ${theme.bodyText}`}>
            {driversBody}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4">
        <div
          className={`h-2 rounded-full overflow-hidden ${theme.barTrack}`}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pctRemaining)}
          data-testid="expiry-card-progress"
        >
          <div
            className={`h-full ${theme.barFill} transition-[width] duration-1000 ease-linear`}
            style={{ width: `${pctRemaining}%` }}
          />
        </div>
      </div>

      {/* Headline + remaining line */}
      <div className="mt-3 space-y-1">
        <p className={`text-xs sm:text-sm font-medium ${theme.bodyText}`} data-testid="expiry-card-headline">
          {headline}
        </p>
        <p className="text-xs text-gray-500" data-testid="expiry-card-remaining">
          You have <span className={`font-semibold ${theme.highlight}`}>{remainingPretty}</span> to choose a driver.
        </p>
      </div>
    </div>
  );
}
